/**
 * Recharge product-catalog helper. Extracted from cart-drawer.js so the
 * subscription/pricing logic that depends on the Recharge CDN catalog is
 * decoupled from cart-drawer rendering and can be reused + unit-tested.
 *
 * Exposes `window.Recharge` (used by assets/cart-drawer.js and assets/cart-page.js):
 *   - getProducts()          -> Promise<Array>  cached CDN catalog (mem → LS → net)
 *   - mapCartItems(items, p) -> Array           cart lines enriched with variants
 *
 * Back-compat: continues to populate window.RechargeCustomWidget.rc_cdn_products,
 * which snippets/recharge_global.liquid and snippets/site-discount-bar-old.liquid
 * (its unused switchAllProduct) still read directly.
 */
(function () {
    'use strict';

    const PRODUCTS_TTL_MS = 24 * 60 * 60 * 1000; // 24h
    const CACHE_KEY = 'rc_products';
    const TS_KEY = 'recharge-ts';

    // De-dupes concurrent CDN fetches across all callers.
    let inFlight = null;

    function cacheInMemory(products) {
        window.RechargeCustomWidget = {
            ...(window.RechargeCustomWidget || {}),
            rc_cdn_products: products,
        };
    }

    // Recharge CDN product catalog. In-memory first, then localStorage with a
    // 24h TTL, then network. Concurrent callers share the in-flight promise.
    async function getProducts() {
        if (window.RechargeCustomWidget?.rc_cdn_products) {
            return window.RechargeCustomWidget.rc_cdn_products;
        }

        const cachedTs = Number(localStorage.getItem(TS_KEY));
        const cachedRaw = localStorage.getItem(CACHE_KEY);
        if (cachedRaw && cachedTs && Date.now() - cachedTs < PRODUCTS_TTL_MS) {
            try {
                const cached = JSON.parse(cachedRaw);
                cacheInMemory(cached);
                return cached;
            } catch (e) {
                console.error('[recharge] failed to parse cached products', e);
            }
        }

        if (inFlight) return inFlight;

        inFlight = (async () => {
            try {
                const response = await fetch(
                    `https://static.rechargecdn.com/store/${window.Shopify.shop}/product/2020-12/products.json`
                );
                const { products } = await response.json();
                const list = products ?? [];
                localStorage.setItem(CACHE_KEY, JSON.stringify(list));
                localStorage.setItem(TS_KEY, String(Date.now()));
                cacheInMemory(list);
                return list;
            } catch (e) {
                console.error('[recharge] failed to fetch products', e);
                return [];
            } finally {
                inFlight = null;
            }
        })();

        return inFlight;
    }

    // Pure: enrich each cart line with its Recharge variant list (for selling
    // plan lookup). Lines with no matching CDN product pass through untouched.
    function mapCartItems(items, cdnProducts) {
        return items.map((item) => {
            const rcProd = cdnProducts.find((p) => item.product_id === Number(Object.keys(p)[0]));
            if (!rcProd) return item;
            return { ...item, variants: Object.values(rcProd)[0].variants };
        });
    }

    window.Recharge = { getProducts, mapCartItems, cacheInMemory };
})();
